import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { useBahasa } from '../../../app/i18n'
import {
  BATAS_AUDIO, useBuatMeeting, useHapusRekaman, usePercakapan, useProspekSatu,
  useTambahCatatan, useTim, useUbahHasil, useUbahJadwal, useUbahPeserta, useUbahProspek,
  useUnggahRekaman,
} from '../../../domains/prospect/api'
import { istilah } from '../../../shared/lib/istilah'
import { jam, sejak, tanggal, tanggalJam, waktuTenantKeISO, waktuTenantUntukIsian } from '../../../shared/lib/waktu'
import {
  AreaTeks, Bendera, Body, Chip, Gagal, Isian, KV, KVBaris, KepalaHalaman,
  Kolom2, Kosong, Memuat, Panel, Pilihan, Sub, Tab, Tabel, Tombol,
} from '../../../shared/ui'
import { jelaskanRekaman, pasangkanRekaman } from '../../../domains/prospect/rekaman'
import { barisData } from '../../../domains/prospect/tampil'

export { jelaskanRekaman, pasangkanRekaman, pecahRekaman } from '../../../domains/prospect/rekaman'
export { barisData } from '../../../domains/prospect/tampil'

/**
 * Meeting BERIKUTNYA — yang akan datang dan paling dekat.
 *
 * Bukan yang terbaru dibuat, dan bukan yang paling akhir di daftar. Pertanyaan yang
 * dibawa orang saat membuka tab ini cuma satu — "kapan saya ketemu mereka lagi" — dan
 * meeting yang sudah lewat tidak menjawabnya.
 *
 * Yang DIBATALKAN dikecualikan: ia masih punya tanggal di masa depan, jadi tanpa
 * penyaring ini strip fakta akan menyebut pertemuan yang justru sudah dibatalkan
 * seseorang — kesalahan yang terbaca sebagai jadwal yang masih berlaku.
 *
 * DIPISAH JADI FUNGSI MURNI supaya bisa diuji: isi tab hanya dirender sesudah tabnya
 * diklik, dan `renderToString` tidak mengklik apa pun (XXIII-1).
 */
export function meetingBerikutnya(meetings, sekarang = new Date()) {
  return (meetings ?? [])
    .filter((m) => m.confirmed_at && new Date(m.confirmed_at) >= sekarang
                   && m.status !== 'cancelled')
    .sort((a, b) => new Date(a.confirmed_at) - new Date(b.confirmed_at))[0] || null
}

/** Riwayat dibaca mundur: terbaru dulu. Yang belum berjadwal jatuh ke bawah. */
export function meetingTerbaruDulu(meetings) {
  return [...(meetings ?? [])].sort(
    (a, b) => new Date(b.confirmed_at || 0) - new Date(a.confirmed_at || 0))
}

/**
 * Meeting mana yang TERBUKA saat tab Meeting pertama kali dilihat.
 *
 * Daftar yang seluruhnya tertutup memaksa satu klik sebelum menampilkan apa pun, dan
 * yang dicari orang saat membuka tab ini hampir selalu satu hal: pertemuan berikutnya.
 * Kalau tidak ada lagi yang akan datang, yang terakhir terjadi — karena pertanyaannya
 * berubah jadi "kemarin hasilnya apa".
 *
 * `dipilih` adalah pilihan ORANGNYA dan selalu menang, termasuk saat ia menutup
 * semuanya: `new Set()` kosong berbeda dari `null` yang berarti belum menyentuh apa pun.
 * Tanpa perbedaan itu, panel yang baru ditutup akan terbuka sendiri lagi.
 *
 * Murni, karena `renderToString` tidak mengklik (XXIII-1) — dan karena inilah satu-
 * satunya alasan uji render bisa melihat isi sebuah meeting sama sekali.
 */
export function meetingTerbuka(meetings, dipilih, sekarang = new Date()) {
  if (dipilih) return dipilih
  const awal = meetingBerikutnya(meetings, sekarang) || meetingTerbaruDulu(meetings)[0]
  return new Set(awal ? [awal.id] : [])
}

/** Bentuk meeting jadi satu frasa: "Online — link" / "Offline di Gedung A". */
export function bentukMeeting(m, t) {
  if (m.meeting_format === 'online') {
    return m.meeting_link ? `${t('prospek.online')} — ${m.meeting_link}` : t('prospek.online')
  }
  if (m.meeting_format === 'offline') {
    return m.location ? `${t('prospek.offline')} · ${m.location}` : t('prospek.offline')
  }
  return t('prospek.bentukBelum')
}

/** Nama yang menjalankan meeting: pesertanya.
 *
 *  Pemegang meeting (`assigned_to_name`) DICABUT 29 September (revisi Telegram R7):
 *  peserta satu-satunya penanggung jawab. Kolomnya tetap ada di data, tapi tidak lagi
 *  dibaca — meeting lama tanpa peserta tampil "belum ada peserta", dan itu memang
 *  keadaannya.
 */
export function namaPeserta(m) {
  const n = (m.assignees ?? []).map((a) => a.name || a.nama).filter(Boolean)
  return n.join(', ')
}

/**
 * Peserta yang PUNYA AKUN, sebagai daftar id — bentuk yang dimengerti pemilihnya.
 *
 * Peserta hasil klaim Telegram tidak punya `web_user_id`, jadi tidak ada kotak centang
 * yang bisa mewakilinya. Ia sengaja tidak ikut: mencentangnya mustahil, dan menampilkan
 * centang yang tidak bisa dicabut lebih buruk daripada tidak menampilkannya.
 */
export function idPeserta(m) {
  return (m.assignees ?? []).map((a) => a.web_user_id).filter(Boolean).map(String)
}

/**
 * "6 dari 7 terisi" — yang DIHITUNG hanya field yang masih dikumpulkan.
 *
 * Field yang sudah dinonaktifkan tetap ditampilkan (isiannya masih ada), tapi
 * memasukkannya ke penyebut membuat prospek yang datanya lengkap terlihat kurang
 * selamanya — dan tidak ada satu pun tindakan yang bisa melengkapinya.
 */
export function ringkasData(baris) {
  const hidup = (baris ?? []).filter((b) => b.aktif)
  return { terisi: hidup.filter((b) => b.nilai).length, total: hidup.length }
}

/** Baris label + kendali, dipakai keempat form di layar ini. */
function Isi({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-[.06em] text-ink3">
        {label}
      </span>
      {children}
    </label>
  )
}

/**
 * UBAH DATA — satu isian per pertanyaan tenant, bukan lima medan yang dipatok.
 *
 * Yang TIDAK dikirim adalah medan yang tidak disentuh, bukan medan yang dikosongkan.
 * `PATCH` menerima sebagian, jadi mengirim seluruh formulir apa adanya akan menimpa
 * nilai yang tidak pernah dibuka orangnya dengan string kosong.
 *
 * Kolom bawaan dan field tambahan berangkat lewat jalan berbeda — yang pertama medan
 * biasa, yang kedua di bawah `custom_fields` — dan itu satu-satunya tempat di layar ini
 * yang perlu tahu bedanya.
 */
const KUNCI_BAWAAN = ['company_name', 'company_address', 'pic_name', 'pic_email', 'pic_phone']

export function FormUbahData({ baris, t, simpan, tutup }) {
  const bisaDiubah = baris.filter((b) => b.aktif)
  const [f, setF] = useState(
    () => Object.fromEntries(bisaDiubah.map((b) => [b.kunci, b.nilai || ''])))
  const semula = Object.fromEntries(bisaDiubah.map((b) => [b.kunci, b.nilai || '']))
  const kirim = () => {
    const isi = {}
    const tambahan = {}
    for (const [k, v] of Object.entries(f)) {
      const bersih = (v || '').trim()
      if (bersih === (semula[k] ?? '')) continue
      if (KUNCI_BAWAAN.includes(k)) isi[k] = bersih || null
      else tambahan[k] = bersih
    }
    if (Object.keys(tambahan).length) isi.custom_fields = tambahan
    if (Object.keys(isi).length === 0) return tutup()
    simpan.mutate(isi, { onSuccess: tutup })
  }
  return (
    <div className="flex flex-col gap-2.5 max-w-[52ch]">
      {bisaDiubah.map((b) => (
        <Isi key={b.kunci} label={b.label}>
          <Isian value={f[b.kunci] ?? ''}
                 onChange={(e) => setF((x) => ({ ...x, [b.kunci]: e.target.value }))} />
        </Isi>
      ))}
      <span className="flex gap-2">
        <Tombol kecil disabled={simpan.isPending} onClick={kirim}>
          {simpan.isPending ? t('umum.menyimpan') : t('umum.simpanPerubahan')}
        </Tombol>
        <Tombol kecil jenis="sekunder" onClick={tutup}>{t('umum.batal')}</Tombol>
      </span>
      {simpan.isError && <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>}
      <Sub>{t('prospek.ubahCatatan')}</Sub>
    </div>
  )
}

/** TAMBAH CATATAN — judul wajib, tanggal bawaan hari ini, keterangan opsional. */
export function FormCatatan({ t, simpan, tutup }) {
  const hariIni = new Date().toISOString().slice(0, 10)
  const [f, setF] = useState({ title: '', event_date: hariIni, notes: '' })
  const bisa = f.title.trim().length > 0 && !!f.event_date
  return (
    <div className="flex flex-col gap-2.5 max-w-[52ch]">
      <Isi label={t('prospek.cJudul')}>
        <Isian value={f.title} placeholder={t('prospek.cContoh')}
               onChange={(e) => setF({ ...f, title: e.target.value })} />
      </Isi>
      {/* TANGGAL TERPISAH dari "kapan dicatat": orang menuliskan telepon kemarin pada
          hari ini, dan kronologi yang memakai waktu penulisan akan mengurutkannya salah. */}
      <Isi label={t('prospek.cKapan')}>
        <Isian type="date" value={f.event_date}
               onChange={(e) => setF({ ...f, event_date: e.target.value })} />
      </Isi>
      <Isi label={t('prospek.cKeterangan')}>
        <AreaTeks baris={3} value={f.notes}
                  onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </Isi>
      <span className="flex gap-2">
        <Tombol kecil disabled={!bisa || simpan.isPending}
                onClick={() => simpan.mutate(
                  { title: f.title.trim(), event_date: f.event_date,
                    notes: f.notes.trim() || null }, { onSuccess: tutup })}>
          {simpan.isPending ? t('umum.menyimpan') : t('prospek.simpanCatatan')}
        </Tombol>
        <Tombol kecil jenis="sekunder" onClick={tutup}>{t('umum.batal')}</Tombol>
      </span>
      {simpan.isError && <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>}
    </div>
  )
}

/**
 * JADWALKAN MEETING.
 *
 * Bentuknya menentukan medan berikutnya: online butuh tautan, offline butuh lokasi.
 * Menampilkan keduanya sekaligus berarti setengah formulir selalu tidak relevan, dan
 * yang tidak relevan tetap terisi kalau orangnya berganti pikiran di tengah.
 */
export function FormMeeting({ m = null, t, simpan, tutup }) {
  const [f, setF] = useState(() => {
    const waktu = waktuTenantUntukIsian(m?.confirmed_at)
    return { tanggal: waktu.slice(0, 10), jam: waktu.slice(11, 16) || '10:00',
             bentuk: m ? (m.meeting_format || '') : 'online', tautan: m?.meeting_link || '',
             lokasi: m?.location || '', agenda: m?.agenda || '' }
  })
  const [koreksi, setKoreksi] = useState(false)
  const [galatWaktu, setGalatWaktu] = useState(false)
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }))
  const adaWaktu = !!f.tanggal && !!f.jam
  const bisa = m && !m.confirmed_at ? true : adaWaktu
  const waktuLama = waktuTenantUntukIsian(m?.confirmed_at)
  const waktuDiubah = !!m && adaWaktu && `${f.tanggal}T${f.jam}` !== waktuLama
  const waktuLamaLewat = !!m?.confirmed_at && new Date(m.confirmed_at) < new Date()
  const kirim = () => {
    // Jam yang ditulis adalah jam tenant, sama dengan kartu meeting. Zona peramban
    // operator bisa berbeda, jadi jangan biarkan Date lokal menggeser jadwal.
    let confirmedAt
    try {
      confirmedAt = adaWaktu ? waktuTenantKeISO(`${f.tanggal}T${f.jam}`) : null
      setGalatWaktu(false)
    } catch {
      setGalatWaktu(true)
      return
    }
    const isi = {
      ...(adaWaktu ? { confirmed_at: confirmedAt } : {}),
      meeting_format: f.bentuk || null,
      meeting_link: f.bentuk === 'online' ? (f.tautan.trim() || null) : null,
      location: f.bentuk === 'offline' ? (f.lokasi.trim() || null) : null,
      agenda: f.agenda.trim() || null,
    }
    if (m) {
      // PATCH hanya perubahan. Agenda saja tidak boleh memicu notifikasi jadwal.
      for (const k of Object.keys(isi)) {
        if (k === 'confirmed_at' ? !waktuDiubah : isi[k] === (m[k] || null)) delete isi[k]
      }
      isi.meetingId = m.id
      if (waktuDiubah && koreksi) isi.correction = true
    }
    simpan.mutate(isi, { onSuccess: tutup })
  }
  return (
    <div className="flex flex-col gap-2.5 max-w-[52ch]">
      <div className="grid grid-cols-2 gap-2.5">
        <Isi label={t('prospek.mTanggal')}>
          <Isian type="date" value={f.tanggal} onChange={(e) => set('tanggal', e.target.value)} />
        </Isi>
        <Isi label={t('prospek.mJam')}>
          <Isian type="time" value={f.jam} onChange={(e) => set('jam', e.target.value)} />
        </Isi>
      </div>
      <Isi label={t('prospek.mBentuk')}>
        <Pilihan value={f.bentuk} onChange={(e) => set('bentuk', e.target.value)}>
          {m && <option value="">{t('prospek.bentukBelum')}</option>}
          <option value="online">{t('prospek.online')}</option>
          <option value="offline">{t('prospek.offline')}</option>
        </Pilihan>
      </Isi>
      {f.bentuk === 'online' ? (
        <Isi label={t('prospek.mTautan')}>
          <Isian value={f.tautan} placeholder="https://meet.google.com/..."
                 onChange={(e) => set('tautan', e.target.value)} />
        </Isi>
      ) : f.bentuk === 'offline' ? (
        <Isi label={t('prospek.mLokasi')}>
          <Isian value={f.lokasi} onChange={(e) => set('lokasi', e.target.value)} />
        </Isi>
      ) : null}
      <Isi label={t('prospek.mAgenda')}>
        <AreaTeks baris={2} value={f.agenda} onChange={(e) => set('agenda', e.target.value)} />
      </Isi>
      {waktuLamaLewat && waktuDiubah && (
        <label className="flex items-center gap-2 text-[12.5px]">
          <input type="checkbox" checked={koreksi} onChange={(e) => setKoreksi(e.target.checked)} />
          {t('prospek.koreksiJadwal')}
        </label>
      )}
      <span className="flex gap-2">
        <Tombol kecil disabled={!bisa || simpan.isPending || (waktuLamaLewat && waktuDiubah && !koreksi)} onClick={kirim}>
          {simpan.isPending ? t('umum.menyimpan') : t(m ? 'umum.simpan' : 'prospek.simpanMeeting')}
        </Tombol>
        <Tombol kecil jenis="sekunder" onClick={tutup}>{t('umum.batal')}</Tombol>
      </span>
      {simpan.isError && <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>}
      {galatWaktu && <Bendera nada="bad" role="alert">{t('prospek.waktuTidakAda')}</Bendera>}
    </div>
  )
}

/** PESERTA — daftar centang, dikirim UTUH karena backend menimpa seluruh daftarnya. */
function FormPeserta({ m, t, tim, simpan, tutup }) {
  const [pilih, setPilih] = useState(() => new Set(idPeserta(m)))
  const geser = (id) => setPilih((s) => {
    const baru = new Set(s)
    if (baru.has(id)) baru.delete(id); else baru.add(id)
    return baru
  })
  return (
    <div className="flex flex-col gap-2">
      {tim.isLoading && <Memuat tinggi={2} />}
      {tim.isError && <Gagal galat={tim.error} ulangi={tim.refetch} />}
      {tim.data?.length === 0 && <Sub>{t('prospek.timKosong')}</Sub>}
      {tim.data?.map((o) => (
        <label key={o.id} className="flex items-center gap-2 text-[12.5px] cursor-pointer">
          <input type="checkbox" checked={pilih.has(String(o.id))}
                 onChange={() => geser(String(o.id))} />
          {o.nama}
        </label>
      ))}
      <span className="flex gap-2 mt-1">
        <Tombol kecil disabled={simpan.isPending}
                onClick={() => simpan.mutate(
                  { meetingId: m.id, ids: [...pilih] }, { onSuccess: tutup })}>
          {simpan.isPending ? t('umum.menyimpan') : t('prospek.simpanPeserta')}
        </Tombol>
        <Tombol kecil jenis="sekunder" onClick={tutup}>{t('umum.batal')}</Tombol>
      </span>
      {simpan.isError && <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>}
      <Sub>{t('prospek.pesertaKet')}</Sub>
    </div>
  )
}

/** HASIL — apa yang terjadi di pertemuan itu. Tidak memberi tahu siapa pun. */
function FormHasil({ m, t, simpan, tutup }) {
  const [notes, setNotes] = useState(m.notes || '')
  return (
    <div className="flex flex-col gap-2 max-w-[52ch]">
      <AreaTeks baris={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <span className="flex gap-2">
        <Tombol kecil disabled={simpan.isPending}
                onClick={() => simpan.mutate(
                  { meetingId: m.id, notes: notes.trim() || null }, { onSuccess: tutup })}>
          {simpan.isPending ? t('umum.menyimpan') : t('prospek.simpanHasil')}
        </Tombol>
        <Tombol kecil jenis="sekunder" onClick={tutup}>{t('umum.batal')}</Tombol>
      </span>
      {simpan.isError && <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>}
    </div>
  )
}

// Gaya <Tombol> untuk elemen yang BUKAN tombol — sebuah <Link>. Disalin, bukan
// dibungkus, karena <button> di dalam <a> bukan HTML yang sah.
const GAYA_TOMBOL_UTAMA =
  'inline-block font-semibold rounded border cursor-pointer text-[12px] px-2.5 py-1.5 '
  + 'no-underline bg-accent text-accentInk border-accent'
const GAYA_TOMBOL_KECIL =
  'inline-block font-semibold rounded border cursor-pointer text-[11px] px-2 py-1 '
  + 'no-underline bg-surface text-ink2 border-lineStrong hover:text-ink hover:border-ink3'

/** Kosong ditulis, bukan dibiarkan kosong — sel kosong tidak bisa dibedakan dari cacat. */
function Belum({ children }) {
  return <span className="text-ink3">{children}</span>
}

function Fakta({ k, v }) {
  return (
    <div className="flex-1 min-w-[132px] px-3.5 py-2.5 flex flex-col gap-0.5 border-l border-line first:border-l-0">
      <span className="text-[10px] font-semibold tracking-[.07em] uppercase text-ink3">{k}</span>
      <span className="text-[12.5px] font-semibold">{v}</span>
    </div>
  )
}

/**
 * SATU MEETING, bisa dipencet dan mengembang.
 *
 * Ringkasannya sebaris; hasilnya, pesertanya, dan rekamannya ada di dalam. Bentuk lama
 * memakai tabel plus satu kartu terpisah untuk pertemuan berikutnya — dan kartu itu cuma
 * bisa memperlihatkan SATU meeting, jadi rekaman pertemuan bulan lalu tidak punya tempat
 * sama sekali di layar.
 */
export function KartuMeeting({ m, t, locale, terbuka, geser, tim, peserta, jadwal, hasil, rekaman,
                              hapus }) {
  const [sunting, setSunting] = useState(null)
  // Nomor rekaman yang menunggu konfirmasi hapus. Satu saja: dua konfirmasi terbuka
  // bersamaan berarti dua tombol "Ya, hapus" yang mirip di kolom sempit yang sama.
  const [mintaHapus, setMintaHapus] = useState(null)
  // KEDUA KOLOM dipecah, lalu disandingkan per nomor. Ringkasan dan transkrip
  // disambung dengan pemisah yang sama dan bernomor sejajar, jadi bagian ke-n dari
  // keduanya memang berasal dari rekaman yang sama.
  const bagian = pasangkanRekaman(m.recording_summary, m.recording_transcript)
  const lewat = m.confirmed_at && new Date(m.confirmed_at) < new Date()
  return (
    <div className="border-b border-line last:border-b-0">
      <button
        type="button"
        onClick={geser}
        aria-expanded={terbuka}
        className="w-full text-left px-3.5 py-2.5 flex items-start gap-2.5 bg-transparent border-0 cursor-pointer hover:bg-surface2"
      >
        <span aria-hidden className="text-[10px] text-ink3 pt-1 w-3 shrink-0">
          {terbuka ? '▾' : '▸'}
        </span>
        <span className="min-w-0 flex-1 grid gap-x-3 gap-y-0.5 sm:grid-cols-[142px_112px_minmax(0,1fr)_minmax(0,1.2fr)_auto] sm:items-baseline">
          <span className="font-mono text-[12px]">
            {m.confirmed_at ? tanggal(m.confirmed_at, locale)
                            : <Belum>{t('prospek.belumDijadwalkan')}</Belum>}
            {m.confirmed_at && (
              <span className="block text-[11px] text-ink3">
                {jam(m.confirmed_at, locale)} · {istilah(t, 'meetingStatus', m.status)}
              </span>
            )}
          </span>
          <span className="hidden sm:block text-[12.5px] truncate">{bentukMeeting(m, t)}</span>
          <span className="hidden sm:block text-[12.5px] truncate">
            {namaPeserta(m) || <Belum>—</Belum>}
          </span>
          <span className="hidden sm:block text-[12.5px] truncate">
            {m.notes || <Belum>{t('prospek.belumAdaHasil')}</Belum>}
          </span>
          {/* TIGA KEADAAN, bukan dua. "Belum" berbeda dari "tidak akan pernah": meeting
              yang sudah lewat tanpa rekaman adalah kesempatan yang hilang, yang akan
              datang belum waktunya. */}
          <span className="justify-self-start sm:justify-self-end">
            {bagian.length ? <Chip nada="ok">{t('prospek.adaRingkasan')}</Chip>
              : lewat ? <Chip nada="idle">{t('prospek.tanpaRekaman')}</Chip>
                : <span className="text-[11.5px] text-ink3">—</span>}
          </span>
        </span>
      </button>

      {terbuka && (
        <div className="px-3.5 pb-3.5 pl-[34px] flex flex-col gap-3.5">
          <KV>
            {sunting === 'jadwal' ? (
              <KVBaris label={t('prospek.mKapan')}>
                <FormMeeting m={m} t={t} simpan={jadwal} tutup={() => setSunting(null)} />
              </KVBaris>
            ) : (
              <>
                <KVBaris label={t('prospek.mKapan')}>
                  <span className="flex flex-wrap items-baseline gap-2">
                    {m.confirmed_at ? tanggalJam(m.confirmed_at, locale)
                                    : <Belum>{t('prospek.belumDijadwalkan')}</Belum>}
                    <Tombol kecil jenis="sekunder" onClick={() => setSunting('jadwal')}>
                      {t('umum.sunting')}
                    </Tombol>
                  </span>
                </KVBaris>
                <KVBaris label={t('prospek.mBentuk')}>{bentukMeeting(m, t)}</KVBaris>
              </>
            )}
            <KVBaris label={t('prospek.mSiapa')}>
              {sunting === 'peserta' ? (
                <FormPeserta m={m} t={t} tim={tim} simpan={peserta}
                             tutup={() => setSunting(null)} />
              ) : (
                <span className="flex flex-wrap items-center gap-1.5">
                  {(m.assignees ?? []).length === 0
                    ? <Belum>{t('prospek.tanpaPeserta')}</Belum>
                    : (m.assignees ?? []).map((a, i) => (
                        <Chip key={a.web_user_id || a.name || i} nada="accent">{a.name}</Chip>
                      ))}
                  <Tombol kecil jenis="sekunder" onClick={() => setSunting('peserta')}>
                    {t('umum.sunting')}
                  </Tombol>
                </span>
              )}
            </KVBaris>
            <KVBaris label={t('prospek.mAgenda')}>
              <span className="flex flex-wrap items-baseline gap-2">
                {m.agenda || <Belum>—</Belum>}
                {sunting !== 'jadwal' && (
                  <Tombol kecil jenis="sekunder" onClick={() => setSunting('jadwal')}>
                    {t('umum.sunting')}
                  </Tombol>
                )}
              </span>
            </KVBaris>
            <KVBaris label={t('prospek.mHasil')}>
              {sunting === 'hasil' ? (
                <FormHasil m={m} t={t} simpan={hasil} tutup={() => setSunting(null)} />
              ) : (
                <span className="flex flex-wrap items-baseline gap-2">
                  {m.notes || <Belum>{t('prospek.hasilBelum')}</Belum>}
                  <Tombol kecil jenis="sekunder" onClick={() => setSunting('hasil')}>
                    {t('umum.sunting')}
                  </Tombol>
                </span>
              )}
            </KVBaris>
          </KV>

          {/* REKAMAN — bisa lebih dari satu untuk pertemuan yang sama: terputus di
              tengah, dilanjutkan sesudah istirahat, atau peserta kedua merekam
              bagiannya sendiri. Backend menyambung, layar ini memecahnya kembali. */}
          <div className="border-t border-line pt-3 flex flex-col gap-2">
            <span className="flex items-baseline justify-between gap-3">
              <b className="text-[12.5px]">{t('prospek.mRekaman')}</b>
              {bagian.length > 0 && <Chip nada="ok">{t('prospek.rekamanJumlah', { n: bagian.length })}</Chip>}
            </span>
            {bagian.length === 0 && <Sub>{t('prospek.belumAdaRekaman')}</Sub>}
            {bagian.map((b) => (
              <div key={b.n} className="bg-surface2 border border-line rounded px-3 py-2">
                <span className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-[.07em] text-ink3">
                    {t('prospek.rekamanKe', { n: b.n })}
                    {/* Rekaman lama tersimpan tanpa jam — dikatakan apa adanya, bukan
                        ditambal jam karangan dari `recorded_at`, yang memuat waktu
                        rekaman TERAKHIR dan karena itu salah untuk yang pertama. */}
                    {b.kapan ? ` · ${b.kapan}` : ` · ${t('prospek.jamTidakTercatat')}`}
                  </span>
                  <Tombol kecil jenis="sekunder" disabled={hapus.isPending}
                          onClick={() => setMintaHapus(b.n)}>
                    {t('umum.hapus')}
                  </Tombol>
                </span>
                {/* HAPUS SELALU DIKONFIRMASI. Rekaman tidak bisa dikembalikan — audionya
                    tidak disimpan, cuma hasil transkripsinya — jadi satu klik keliru
                    menghapus satu-satunya salinan yang ada. */}
                {mintaHapus === b.n && (
                  <span className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="text-[12px] text-ink2">{t('prospek.hapusRekamanYakin')}</span>
                    <Tombol kecil disabled={hapus.isPending}
                            onClick={() => hapus.mutate({ meetingId: m.id, nomor: b.n },
                                                        { onSuccess: () => setMintaHapus(null) })}>
                      {hapus.isPending ? t('umum.menyimpan') : t('prospek.hapusRekamanIya')}
                    </Tombol>
                    <Tombol kecil jenis="sekunder" onClick={() => setMintaHapus(null)}>
                      {t('umum.batal')}
                    </Tombol>
                  </span>
                )}
                {b.isi
                  ? <p className="m-0 mt-1 text-[12.5px] whitespace-pre-wrap">{b.isi}</p>
                  : <p className="m-0 mt-1 text-[12.5px] text-ink3">{t('prospek.ringkasanGagal')}</p>}
                {/* TRANSKRIP DITUTUP secara bawaan. Ia panjang dan mentah; yang dicari
                    orang hampir selalu ringkasannya, dan transkrip yang terbuka mendorong
                    ringkasan rekaman berikutnya keluar layar. */}
                {b.transkrip && (
                  <details className="mt-2">
                    <summary className="text-[11px] text-ink2 cursor-pointer">
                      {t('prospek.lihatTranskrip')}
                    </summary>
                    <p className="m-0 mt-1.5 text-[12px] text-ink2 whitespace-pre-wrap">
                      {b.transkrip}
                    </p>
                  </details>
                )}
              </div>
            ))}
            {hapus.isError && <Bendera nada="bad" role="alert">{hapus.error?.message}</Bendera>}
            <label className="flex flex-wrap items-center gap-2 text-[12px]">
              <span className="font-semibold rounded border cursor-pointer text-[11px] px-2 py-1 bg-surface text-ink2 border-lineStrong hover:text-ink hover:border-ink3">
                {rekaman.isPending ? t('prospek.mengunggah') : t('prospek.unggahRekaman')}
              </span>
              <input
                type="file"
                accept="audio/*"
                className="hidden"
                disabled={rekaman.isPending}
                onChange={(e) => {
                  const berkas = e.target.files?.[0]
                  // Isian direset supaya berkas YANG SAMA bisa dipilih lagi sesudah
                  // gagal — `change` tidak menyala kalau nilainya tidak berubah.
                  e.target.value = ''
                  if (berkas) rekaman.mutate({ meetingId: m.id, berkas })
                }}
              />
            </label>
            {rekaman.isError && (
              <Bendera nada="bad" role="alert">{jelaskanRekaman(rekaman.error, t)}</Bendera>
            )}
            <Sub>{t('prospek.rekamanKet', { mb: Math.round(BATAS_AUDIO / 100_000) / 10 })}</Sub>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * ISI TAB PERCAKAPAN — komponen tersendiri supaya uji render bisa menyentuhnya.
 *
 * Tab hanya dirender sesudah diklik, dan `renderToString` tidak mengklik apa pun
 * (XXIII-1). Selama isinya tinggal di dalam induknya, ia tidak bisa dibuktikan tersusun
 * oleh apa pun kecuali mata — dan itu persis keadaan yang membuat panel ini salah selama
 * ini tanpa satu pun pemeriksa mengeluh.
 */
export function IsiPercakapan({ p, percakapan, dipegang, penangan, ubah, t, locale }) {
  return (
    <div className="flex flex-col gap-3.5">
      <Panel judul={t('prospek.riwayatDM')} catatan={t('prospek.riwayatDMCatatan')}>
        {percakapan.isLoading && <Memuat />}
        {percakapan.isError && <Gagal galat={percakapan.error} ulangi={percakapan.refetch} />}
        {percakapan.data && percakapan.data.length === 0 && (
          <Kosong judul={t('prospek.dmKosong')} keterangan={t('prospek.dmKosongKet')} />
        )}
        {percakapan.data?.length > 0 && (
          <Body className="flex flex-col gap-2">
            {percakapan.data.map((m) => (
              <div key={m.id} className="flex flex-col gap-2">
                {m.original_text && (
                  <div className="max-w-[74%] self-start bg-surface2 rounded-lg rounded-bl-xs px-3 py-2 text-[12.5px]">
                    {m.original_text}
                    <span className="block mt-1 text-[10px] text-ink3 font-mono">
                      {p.sender_username ? `@${p.sender_username} · ` : ''}
                      {tanggalJam(m.created_at, locale)}
                    </span>
                  </div>
                )}
                {m.ai_reply && (
                  <div className="max-w-[74%] self-end bg-accentSoft rounded-lg rounded-br-xs px-3 py-2 text-[12.5px]">
                    {m.ai_reply}
                    <span className="block mt-1 text-[10px] text-ink3 font-mono text-right">
                      bot · {tanggalJam(m.created_at, locale)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </Body>
        )}
        {/* MEMBALAS TETAP DI ANTREAN, dan tautannya membawa penyaringnya. Rute balas
            membaca penerimanya dari BARIS antrean dan pemanggilnya tidak punya cara
            menyebutkannya — itu yang menahan "balas DM ini" supaya tidak berubah
            jadi "kirim DM ke siapa saja". Tombol di sini yang mengirim sendiri
            menuntut izin kedua itu. */}
        <Body className="border-t border-line flex flex-wrap items-center gap-2">
          <Link
            to={`/perlu-dijawab${p.sender_username ? `?cari=${encodeURIComponent(p.sender_username)}` : ''}`}
            className={GAYA_TOMBOL_UTAMA}
          >
            {t('prospek.balasDariSini')}
          </Link>
          <span className="text-[11px] text-ink3">{t('prospek.balasDariSiniKet')}</span>
        </Body>
      </Panel>

      {/* BOT DIPEGANG/HIDUP ADA DI TAB PERCAKAPAN, bukan Ikhtisar (operator,
          13 September). Menahan bot adalah keputusan TENTANG PERCAKAPAN — diambil
          sambil membaca apa yang sudah dijawabnya, bukan sambil melihat alamat dan
          NPWP. Di Ikhtisar ia jauh dari satu-satunya hal yang membuat orang
          menekannya. */}
      <Panel
        judul={t('prospek.siapaMembalas')}
        aksi={
          <Chip nada={dipegang ? 'warn' : 'ok'}>
            {dipegang ? t('prospek.botMati') : t('prospek.botJalan')}
          </Chip>
        }
      >
        <Body className="flex flex-col gap-2.5">
          <p className="m-0 text-[12.5px] text-ink2">
            {dipegang ? t('prospek.dipegangKet') : t('prospek.botJalanKet')}
          </p>
          {penangan && <Sub>{t('prospek.pernahMenangani', { siapa: penangan })}</Sub>}
          <span className="flex flex-wrap items-center gap-2">
            <Tombol
              jenis={dipegang ? 'utama' : 'sekunder'}
              disabled={ubah.isPending}
              onClick={() => ubah.mutate({ ai_paused: !dipegang })}
            >
              {dipegang ? t('prospek.lepas') : t('prospek.pegang')}
            </Tombol>
            <span className="text-[11px] text-ink3">{t('prospek.tidakMundur')}</span>
          </span>
          {ubah.isError && <p className="m-0 text-[12px] text-bad">{ubah.error?.message}</p>}
        </Body>
      </Panel>

      <Bendera nada="info" judul={t('prospek.ambilVsPegang')}>
        <p>{t('prospek.ambilVsPegangKet')}</p>
      </Bendera>
    </div>
  )
}

export default function ProspekDetail() {
  const { id } = useParams()
  const { t, locale } = useBahasa()
  const [tab, setTab] = useState('data')
  // SATU form terbuka pada satu waktu. Tiga form yang bisa terbuka bersamaan di satu
  // layar sempit berarti tiga tombol Simpan yang mirip, dan yang salah tekan tidak
  // terlihat sebagai kesalahan.
  const [buka, setBuka] = useState(null)
  // `null` = belum menyentuh apa pun, jadi bawaannya berlaku. Lihat `meetingTerbuka`.
  const [dibuka, setDibuka] = useState(null)

  const q = useProspekSatu(id)
  const percakapan = usePercakapan(id)
  const tim = useTim()
  const ubah = useUbahProspek(id)
  const catatan = useTambahCatatan(id)
  const buatMeeting = useBuatMeeting(id)
  const peserta = useUbahPeserta(id)
  const jadwal = useUbahJadwal(id)
  const hasil = useUbahHasil(id)
  const rekaman = useUnggahRekaman(id)
  const hapusRekaman = useHapusRekaman(id)

  if (q.isLoading) return <Memuat tinggi={5} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />
  const p = q.data
  if (!p) return null

  const dipegang = !!p.ai_paused
  const meetings = p.meetings ?? []
  const jmlMeeting = meetings.length
  const berikutnya = meetingBerikutnya(meetings)
  const urut = meetingTerbaruDulu(meetings)
  const terbuka = meetingTerbuka(meetings, dibuka)
  const geser = (mid) => setDibuka(() => {
    const baru = new Set(terbuka)
    if (baru.has(mid)) baru.delete(mid); else baru.add(mid)
    return baru
  })
  const data = barisData(p, t)
  const { terisi, total } = ringkasData(data)
  const penangan = (p.handlers ?? []).join(', ')
  const catatanProses = [...(p.process_events ?? [])].sort(
    (a, b) => new Date(b.event_date || 0) - new Date(a.event_date || 0))

  return (
    <>
      <KepalaHalaman
        // ← SEMUA PROSPEK DI ATAS JUDUL, sesuai rancangan. Bentuk lamanya menaruh tautan
        // kecil di deret aksi kanan, berbaris dengan "Jadwalkan meeting" — jalan keluar
        // yang berdiri di sebelah tindakan yang mengubah data.
        atas={
          // Tautan yang BERGAYA tombol, bukan tombol di dalam tautan: <button> di dalam
          // <a> bukan HTML yang sah, dan peramban boleh membongkarnya jadi dua elemen
          // bersaudara — tautan kosong yang tidak bisa diklik, di sebelah tombol yang
          // tidak menuju ke mana pun.
          <Link
            to="/prospek"
            className={GAYA_TOMBOL_KECIL}
          >
            ← {t('prospek.semuaProspek')}
          </Link>
        }
        judul={p.company_name || '—'}
        keterangan={
          <>
            {p.sender_username
              ? <>{t('prospek.dariInstagram')} <span className="font-mono">@{p.sender_username}</span></>
              : t('prospek.dariManual')}
            {' · '}
            {penangan
              ? t('prospek.ditanganiOleh', { siapa: penangan })
              : t('prospek.belumDitangani')}
          </>
        }
        aksi={
          <Tombol onClick={() => { setTab('meeting'); setBuka(buka === 'meeting' ? null : 'meeting') }}>
            {t('prospek.jadwalkanMeeting')}
          </Tombol>
        }
      />

      {/* Strip fakta: tetap terlihat di semua tab. Yang penting tidak ikut
          tersembunyi di balik tab — itu keluhan yang muncul saat merancang. */}
      <div className="flex flex-wrap border border-line rounded-md bg-surface overflow-hidden">
        <Fakta k={t('prospek.asal')} v={p.source === 'manual' ? t('prospek.manual') : 'Instagram'} />
        <Fakta k={t('prospek.tahap')} v={istilah(t, 'tahap', p.stage) || '—'} />
        <Fakta k={t('prospek.ditangani')} v={penangan || <Belum>—</Belum>} />
        <Fakta
          k={t('prospek.meetingBerikut')}
          v={berikutnya
            ? `${tanggal(berikutnya.confirmed_at, locale)} · ${jam(berikutnya.confirmed_at, locale)}`
            : <Belum>—</Belum>}
        />
        <Fakta k={t('prospek.data')} v={t('prospek.dataTerisi', { terisi, total })} />
        <Fakta
          k={t('masuk.bot')}
          v={
            <Chip nada={dipegang ? 'warn' : 'ok'}>
              {dipegang ? t('masuk.botDipegang') : t('masuk.botHidup')}
            </Chip>
          }
        />
      </div>

      <Tab
        aktif={tab}
        set={setTab}
        daftar={[
          { id: 'data', label: t('prospek.ikhtisar') },
          { id: 'percakapan', label: t('prospek.percakapan') },
          // HITUNGANNYA IKUT DI LABEL. "Meeting" polos tidak membedakan prospek yang
          // punya tiga meeting dari yang belum pernah ketemu sama sekali — dan itu
          // justru pertanyaan pertama saat membuka halaman prospek.
          { id: 'meeting', label: `${t('prospek.meeting')}${jmlMeeting ? ` (${jmlMeeting})` : ''}` },
        ]}
      />

      {tab === 'data' && (
        <Kolom2>
          <Panel
            judul={t('prospek.data')}
            aksi={
              <Tombol kecil jenis="sekunder"
                      onClick={() => setBuka(buka === 'data' ? null : 'data')}>
                {buka === 'data' ? t('umum.batal') : t('prospek.ubahData')}
              </Tombol>
            }
          >
            {/* SUNTING DI TEMPAT, bukan halaman terpisah. Yang diubah medan-medan pendek,
                dan memindahkan orang ke layar lain untuk itu berarti ia kehilangan
                konteks yang justru membuatnya tahu apa yang salah. */}
            {buka === 'data' && (
              <Body className="border-b border-line">
                <FormUbahData baris={data} t={t} simpan={ubah} tutup={() => setBuka(null)} />
              </Body>
            )}
            <Body>
              <KV>
                {data.map((b) => (
                  <KVBaris key={b.kunci} label={b.label}>
                    <span className="flex flex-wrap items-center gap-1.5">
                      {b.nilai || <Belum>{t('prospek.belumDiisi')}</Belum>}
                      {/* Field yang sudah dinonaktifkan tetap tampil beserta isiannya.
                          Menyembunyikannya membuat orang mengira datanya hilang,
                          sementara ia tetap ikut di tiap backup dan tiap ekspor. */}
                      {!b.aktif && <Chip nada="idle">{t('prospek.tidakDikumpulkan')}</Chip>}
                    </span>
                  </KVBaris>
                ))}
              </KV>
              <p className="mt-3 mb-0 text-[11.5px] text-ink3">{t('prospek.dataKet')}</p>
            </Body>
          </Panel>

          {/* CATATAN PROSES adalah apa yang terjadi DI LUAR sistem — ditelepon, kirim
              proposal, ditolak. Sumbernya `process_events`, BUKAN `audit_log`: yang
              kedua ditulis sistem sendiri, dan rancangan menuliskannya persis —
              "diisi tim, bukan bot". */}
          <Panel judul={t('prospek.catatanProses')} catatan={t('prospek.catatanProsesCatatan')}>
            {catatanProses.length === 0 ? (
              <Kosong judul={t('prospek.catatanKosong')}
                      keterangan={t('prospek.catatanKosongKet')} />
            ) : (
              <Tabel kepala={[t('prospek.cTanggal'), t('prospek.cKejadian'), t('prospek.cOleh')]}>
                {catatanProses.map((c) => (
                  <tr key={c.id} className="border-b border-line last:border-b-0 [&>td]:px-3.5 [&>td]:py-2.5 [&>td]:align-top">
                    <td className="font-mono whitespace-nowrap">{tanggal(c.event_date, locale)}</td>
                    <td className="max-w-[320px]">
                      {c.title}
                      {c.notes && <span className="block text-[11.5px] text-ink3">{c.notes}</span>}
                    </td>
                    <td className="whitespace-nowrap">{c.created_by || <Belum>—</Belum>}</td>
                  </tr>
                ))}
              </Tabel>
            )}
            <Body className="border-t border-line">
              {buka === 'catatan' ? (
                <FormCatatan t={t} simpan={catatan} tutup={() => setBuka(null)} />
              ) : (
                <Tombol kecil jenis="sekunder" onClick={() => setBuka('catatan')}>
                  {t('prospek.tambahCatatan')}
                </Tombol>
              )}
            </Body>
          </Panel>
        </Kolom2>
      )}

      {tab === 'percakapan' && (
        <IsiPercakapan
          p={p} percakapan={percakapan} dipegang={dipegang} penangan={penangan}
          ubah={ubah} t={t} locale={locale}
        />
      )}

      {tab === 'meeting' && (
        <div className="flex flex-col gap-3.5">
          {buka === 'meeting' && (
            <Panel judul={t('prospek.jadwalkanMeeting')}>
              <Body>
                <FormMeeting t={t} simpan={buatMeeting} tutup={() => setBuka(null)} />
              </Body>
            </Panel>
          )}

          <Panel
            judul={t('prospek.meetingHitung', { n: jmlMeeting })}
            catatan={t('prospek.meetingCatatan')}
          >
            {jmlMeeting === 0 ? (
              <Kosong judul={t('prospek.meetingKosong')}
                      keterangan={t('prospek.meetingKosongKet')} />
            ) : (
              <>
                <div className="hidden sm:grid gap-x-3 px-3.5 py-2 pl-[34px] border-b border-line grid-cols-[142px_112px_minmax(0,1fr)_minmax(0,1.2fr)_auto] text-[10px] font-semibold tracking-[.08em] uppercase text-ink3">
                  <span>{t('prospek.mKapan')}</span>
                  <span>{t('prospek.mBentuk')}</span>
                  <span>{t('prospek.mSiapa')}</span>
                  <span>{t('prospek.mHasil')}</span>
                  <span className="justify-self-end">{t('prospek.mRekaman')}</span>
                </div>
                {urut.map((m) => (
                  <KartuMeeting
                    key={m.id} m={m} t={t} locale={locale}
                    terbuka={terbuka.has(m.id)} geser={() => geser(m.id)}
                    tim={tim} peserta={peserta} jadwal={jadwal} hasil={hasil} rekaman={rekaman}
                    hapus={hapusRekaman}
                  />
                ))}
              </>
            )}
            <Body className="border-t border-line">
              <Tombol kecil jenis="sekunder"
                      onClick={() => setBuka(buka === 'meeting' ? null : 'meeting')}>
                {t('prospek.jadwalkanLagi')}
              </Tombol>
            </Body>
          </Panel>
        </div>
      )}
    </>
  )
}
